import {useState, useMemo, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import type {TicketLabelPickerQuery as TicketLabelPickerQueryType} from './__generated__/TicketLabelPickerQuery.graphql';

const LabelsQuery = graphql`
	query TicketLabelPickerQuery {
		viewer {
			company {
				labels(first: 10000) {
					edges {
						node {
							id
							name
							color
							category {
								name
							}
						}
					}
				}
			}
		}
	}
`;

interface Props {
	ticketId: string;
	currentLabelIds: string[];
	disabled: boolean;
	onAdd?: (labelId: string) => void;
}

const Container = styled.div`
	position: relative;
	width: 100%;
`;
const Input = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 4px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #1e293b;
	&::placeholder {
		color: #94a3b8;
	}
	&:disabled {
		opacity: 0.5;
	}
`;
const Menu = styled.ul`
	position: absolute;
	right: 0;
	z-index: 10;
	margin: 4px 0 0;
	max-height: 160px;
	width: 100%;
	overflow: auto;
	list-style: none;
	padding: 0;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	${raisedSurfaceShadow}
`;
const Swatch = styled.span<{$color: string}>`
	display: inline-block;
	height: 10px;
	width: 10px;
	border-radius: 9999px;
	background: ${p => p.$color};
`;
const OptionName = styled.span`
	color: #1e293b;
`;
const Category = styled.span`
	margin-left: auto;
	color: #94a3b8;
`;
const EmptyState = styled.div`
	position: absolute;
	right: 0;
	z-index: 10;
	margin-top: 4px;
	width: 100%;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
	${raisedSurfaceShadow}
`;

export function TicketLabelPicker({currentLabelIds, disabled, onAdd}: Props) {
	const data = useLazyLoadQuery<TicketLabelPickerQueryType>(LabelsQuery, {});
	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const currentSet = useMemo(() => new Set(currentLabelIds), [currentLabelIds]);

	const filtered = useMemo(() => {
		const nodes = (data.viewer?.company?.labels?.edges ?? [])
			.map(e => e?.node)
			.filter((n): n is NonNullable<typeof n> => n != null);
		const available = nodes.filter(l => !currentSet.has(l.id));
		if (!query.trim()) return available;
		const lower = query.toLowerCase();
		return available.filter(l => (l.name ?? '').toLowerCase().includes(lower));
	}, [data.viewer?.company?.labels?.edges, currentSet, query]);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<Container ref={containerRef}>
			<Input
				type="text"
				placeholder="Add label..."
				value={query}
				disabled={disabled}
				onChange={e => setQuery(e.target.value)}
				onFocus={() => setOpen(true)}
			/>
			{open && filtered.length > 0 && (
				<Menu>
					{filtered.map(label => (
						<li key={label.id}>
							<Button
								emphasis={EMPHASIS.LOW}
								onPress={() => {
									onAdd?.(label.id);
									setQuery('');
									setOpen(false);
								}}
							>
								<Swatch $color={label.color ?? '#6259ff'} />
								<OptionName>{label.name ?? 'Untitled label'}</OptionName>
								{label.category?.name && <Category>{label.category.name}</Category>}
							</Button>
						</li>
					))}
				</Menu>
			)}
			{open && filtered.length === 0 && query.trim() && <EmptyState>No matching labels</EmptyState>}
		</Container>
	);
}
